import React, { useId } from "react";
import { FaChevronDown, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { Button, Container, Info, Navegacao, SeletorPorPagina } from './styles';

export interface IPaginacao {
  paginaAtual: number;
  totalPaginas: number;
  paginasVisiveis?: number;
  setPagina: (pagina: number) => void;
  /** Total de registros: mostra "1–10 de 234" */
  total?: number;
  porPagina?: number;
  opcoesPorPagina?: number[];
  /** Sem ele, a quantidade por página fica fixa (ex.: vendas/compras, que têm o grid de itens embaixo) */
  setPorPagina?: (porPagina: number) => void;
}

function Paginacao({
  paginaAtual,
  totalPaginas,
  paginasVisiveis = 5,
  setPagina,
  total,
  porPagina,
  opcoesPorPagina = [10, 25, 50, 100],
  setPorPagina
}: IPaginacao) {
  const idSeletor = useId();

  function calculaNumeroPaginas(): number[] {
    const pages: number[] = [];
    const half = Math.floor(paginasVisiveis / 2);
    let start = Math.max(1, paginaAtual - half);
    let end = Math.min(totalPaginas, start + paginasVisiveis - 1);

    if (end - start + 1 < paginasVisiveis) {
      start = Math.max(1, end - paginasVisiveis + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Com total=0 a API devolve {} e total_paginas vem undefined
  if (!totalPaginas) {
    return null;
  }

  const intervalo = (total && porPagina)
    ? `${(paginaAtual - 1) * porPagina + 1}–${Math.min(paginaAtual * porPagina, total)} de ${total}`
    : undefined;

  return (
    <Container aria-label='Paginação'>
      {(setPorPagina || intervalo) &&
        <Info>
          {setPorPagina &&
            <SeletorPorPagina>
              <label htmlFor={idSeletor}>Mostrar</label>
              <span className='campo'>
                <select
                  id={idSeletor}
                  value={porPagina}
                  onChange={(e) => setPorPagina(Number(e.target.value))}
                >
                  {opcoesPorPagina.map(opcao => <option key={opcao} value={opcao}>{opcao}</option>)}
                </select>
                <span className='seta' aria-hidden><FaChevronDown size={10} /></span>
              </span>
              <span>por página</span>
            </SeletorPorPagina>
          }
          {intervalo && <span className='intervalo'>{intervalo}</span>}
        </Info>
      }

      <Navegacao>
        <Button
          onClick={() => setPagina(paginaAtual - 1)}
          disabled={paginaAtual === 1}
          aria-label='Página anterior'
        >
          <FaChevronLeft size={11} /> Anterior
        </Button>

        {calculaNumeroPaginas().map((page) => (
          <Button
            key={page}
            className='numero'
            onClick={() => setPagina(page)} $isActive={page === paginaAtual}
            aria-current={page === paginaAtual ? 'page' : undefined}
          >
            {page}
          </Button>
        ))}

        {/* No mobile os números somem e fica só o resumo */}
        <span className='resumo'>Página {paginaAtual} de {totalPaginas}</span>

        <Button
          onClick={() => setPagina(paginaAtual + 1)}
          disabled={paginaAtual === totalPaginas}
          aria-label='Próxima página'
        >
          Próximo <FaChevronRight size={11} />
        </Button>
      </Navegacao>
    </Container>
  );
};

export default Paginacao;
